import { useEffect, useMemo, useState, type FormEvent } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import { ADMIN_GENDERS } from '@wlb/shared/api/admin/adminTypes';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { DetailList, type DetailRow } from '../../components/ui/DetailList';
import { FormError } from '../../components/ui/FormError';
import { SelectField } from '../../components/ui/SelectField';
import { TextArea } from '../../components/ui/TextArea';
import { TextField } from '../../components/ui/TextField';
import { formatCount, formatDate, formatDateTime } from '../../utils/format';
import {
  changedUserFields,
  toUserFormState,
  useUserDetail,
  type UserFormState,
} from './userForm';
import '../../components/ui/ui.css';
import '../../pages/pages.css';

/** Identity and profile fields. Activation state lives on its own sub-module. */
export function UserProfileTab() {
  const { t } = useTranslation('admin');
  const { user, reload } = useUserDetail();
  const [form, setForm] = useState<UserFormState>(() => toUserFormState(user));
  const [initial, setInitial] = useState<UserFormState>(() => toUserFormState(user));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-syncs after a save or a background refresh brings a newer record.
  useEffect(() => {
    const next = toUserFormState(user);
    setForm(next);
    setInitial(next);
  }, [user]);

  const update = <K extends keyof UserFormState>(key: K, value: UserFormState[K]) => {
    setForm(current => ({ ...current, [key]: value }));
  };

  const payload = changedUserFields(initial, form);
  const hasChanges = Object.keys(payload).length > 0;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasChanges) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await adminService.updateUser(user.userGuid, payload);
      ToastService.show({ type: 'success', message: t('users.toasts.updated') });
      setInitial(form);
      reload();
    } catch (cause) {
      setSaveError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
    } finally {
      setSaving(false);
    }
  };

  const genderOptions = useMemo(
    () => [
      { value: '', label: t('genders.unset') },
      ...ADMIN_GENDERS.map(gender => ({ value: gender, label: t(`genders.${gender}`) })),
    ],
    [t],
  );

  const profileRows = useMemo<readonly DetailRow[]>(
    () => [
      { label: t('users.details.fields.country'), value: user.country },
      {
        label: t('users.details.fields.age'),
        value: user.age == null ? '' : formatCount(user.age),
      },
      { label: t('users.details.fields.dateOfBirth'), value: formatDate(user.dateOfBirth) },
      { label: t('users.details.fields.joined'), value: formatDateTime(user.joiningDate) },
      { label: t('users.details.fields.updated'), value: formatDateTime(user.updatedOn) },
    ],
    [t, user],
  );

  const activityRows = useMemo<readonly DetailRow[]>(
    () => [
      { label: t('users.details.fields.totalPosts'), value: formatCount(user.totalPosts) },
      {
        label: t('users.details.fields.totalVisits'),
        value: formatCount(user.totalVisits),
      },
      { label: t('users.details.fields.firstPost'), value: formatDate(user.firstPostDate) },
      { label: t('users.details.fields.lastPost'), value: formatDate(user.lastPostDate) },
    ],
    [t, user],
  );

  return (
    <div className="page__section">
      <form className="page__panel" onSubmit={onSubmit} noValidate>
        <FormError variant="banner" message={saveError} />
        <div className="page__form-grid">
          <TextField
            label={t('users.details.firstName')}
            value={form.firstName}
            onChange={event => update('firstName', event.target.value)}
          />
          <TextField
            label={t('users.details.lastName')}
            value={form.lastName}
            onChange={event => update('lastName', event.target.value)}
          />
          <TextField
            label={t('users.details.userName')}
            value={form.userName}
            onChange={event => update('userName', event.target.value)}
          />
          <TextField
            label={t('users.details.email')}
            type="email"
            value={form.email}
            onChange={event => update('email', event.target.value)}
          />
          <SelectField
            label={t('users.details.gender')}
            options={genderOptions}
            value={form.gender}
            onChange={event => update('gender', event.target.value)}
          />
        </div>

        <TextArea
          label={t('users.details.aboutMe')}
          placeholder={t('users.details.aboutMePlaceholder')}
          value={form.aboutMe}
          onChange={event => update('aboutMe', event.target.value)}
        />

        <div className="page__panel-actions">
          {/* Disabled rather than silently no-op'ing on an unchanged form. */}
          <Button type="submit" disabled={saving || !hasChanges}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>

      <section className="page__panel" aria-labelledby="user-profile-heading">
        <h2 className="ui-section-title" id="user-profile-heading">
          {t('users.details.sections.profile')}
        </h2>
        <DetailList rows={profileRows} />
      </section>

      <section className="page__panel" aria-labelledby="user-activity-heading">
        <h2 className="ui-section-title" id="user-activity-heading">
          {t('users.details.sections.activity')}
        </h2>
        <DetailList rows={activityRows} />
      </section>
    </div>
  );
}
